import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';

import Skeleton from 'components/Skeleton';
import { isTabletOrMobileDevice } from 'constants/app';
import { MarketGraphDataStatuses } from 'constants/marketGraph';
import { useMarketUnits } from 'hooks/useMarketUnits';
import {
  getIsCurrencyLoaded,
  getIsMarketGraphsDarkThemeEnabled,
  getMarketGraphIsSmallScreenView
} from 'redux/modules/appConfigs/selectors';
import { getMarketDefinitionMarketUnit } from 'redux/modules/marketDefinitions/selectors';
import { setMarketGraphChartStatus } from 'redux/modules/marketGraph';
import {
  getMarketGraphChartIsData,
  getMarketGraphChartIsLoading,
  getMarketGraphIsMarketBettingType
} from 'redux/modules/marketGraph/selectors';
import { getIsUserInfoLoaded } from 'redux/modules/user/selectors';

import MarketGraphChartContent from '../MarketGraphChartContent/MarketGraphChartContent';
import MarketGraphChartInjection from '../MarketGraphChartInjection';
import MarketGraphNoData from '../MarketGraphNoData/MarketGraphNoData';

import commonStyles from '../MarketGraphCommon.module.scss';
import styles from './MarketGraphChart.module.scss';

export default function MarketGraphChart() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { marketId = '', selectionId = '' } = useParams<{ marketId: string; selectionId: string }>();

  const isChartDataLoading = useSelector(getMarketGraphChartIsLoading);
  const isData = useSelector(getMarketGraphChartIsData);
  const isSmallScreenView = useSelector(getMarketGraphIsSmallScreenView);
  const isUserInfoLoaded = useSelector(getIsUserInfoLoaded);
  const isCurrencyLoaded = useSelector(getIsCurrencyLoaded);
  const isDarkTheme = useSelector(getIsMarketGraphsDarkThemeEnabled);
  const isLineMarket = useSelector(getMarketGraphIsMarketBettingType);
  const marketUnit = useSelector(getMarketDefinitionMarketUnit(marketId || ''));

  const [isChartError, setIsChartError] = useState(false);

  const isFirstRender = useRef(true);

  const marketUnitTranslated = useMarketUnits(marketUnit || 'points');

  const isMobile = isSmallScreenView || isTabletOrMobileDevice;
  const isLoading = isChartDataLoading || !isUserInfoLoaded || !isCurrencyLoaded;

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      dispatch(setMarketGraphChartStatus(MarketGraphDataStatuses.pending));
    }
  }, [selectionId]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <>
          <Skeleton
            withBrandingClassName={false}
            className={classNames(commonStyles.skeletons__top, commonStyles.skeleton, {
              [commonStyles.skeletons__top__mobile]: isMobile,
              [commonStyles.skeleton__dark]: isDarkTheme
            })}
          />
          <Skeleton
            withBrandingClassName={false}
            className={classNames(commonStyles.skeletons__bottom, commonStyles.skeleton, {
              [commonStyles.skeletons__bottom__mobile]: isMobile,
              [commonStyles.skeleton__dark]: isDarkTheme
            })}
          />
        </>
      );
    }

    if (isChartError) {
      return (
        <div
          className={classNames(styles.chartError, {
            [styles.chartError__mobile]: isMobile
          })}
        >
          <i className={classNames('biab_custom-icon-error', styles.chartError__icon)} />
          <p
            dangerouslySetInnerHTML={{ __html: t('marketGraph.labels.chartError') }}
            className={classNames(styles.chartError__message, {
              [styles.chartError__message__dark]: isDarkTheme,
              [styles.chartError__message__mobile]: isTabletOrMobileDevice
            })}
          />
        </div>
      );
    }

    if (isData) {
      return <MarketGraphChartContent setIsChartError={setIsChartError} />;
    }

    return <MarketGraphNoData type="chart" />;
  };

  return (
    <div
      className={classNames(styles.chartBlock, {
        [styles.chartBlock__mobile]: isTabletOrMobileDevice,
        [styles.chartBlock__dark]: isDarkTheme
      })}
    >
      <MarketGraphChartInjection />
      <p
        className={classNames(commonStyles.title, {
          [commonStyles.title__mobile]: isTabletOrMobileDevice,
          [commonStyles.title__dark]: isDarkTheme
        })}
      >
        {t('marketGraph.labels.oddsVolumeOverTime', {
          UNITS: isLineMarket && marketUnit ? marketUnitTranslated : t('account.mybets.labels.odds')
        })}
      </p>
      {renderContent()}
    </div>
  );
}
